/**
 * useStaffJobModel.ts
 * The single shared data + write model behind the "Staff this job" v2 view
 * (STAFF-VIEW-V2.md). ONE roster instance + ONE capacity fetch + ONE per-task
 * skill snapshot feed BOTH the task staffing cards (the hero) and the team
 * capacity rail / its detailed weekly expander — so a mentor added from a task
 * card and the % tuned in the expander never drift (they read the same roster).
 *
 * Ownership:
 *   - roster (useSourceRoster): per-week source allocation % + add/set%/remove;
 *     the count-once allocation surface (D2). Task presence ≠ %.
 *   - capacity (fetchEmployeeWeeks): each person's Σ weekly utilization % → the
 *     rail meters + the picker's "who's free" ordering.
 *   - skills (listAssignableUsers(taskTypeKey) + taxonomy skill names): per task
 *     type, everyone's level for the required skill + the skill's display name.
 *   - task writes: assign / remove a person on a specific task (presence via
 *     bulkAssignFromTask / unassignUserFromTask), and the one-click mentor add
 *     (an L3+ onto the source-week roster @25% — materializes task presence).
 *
 * Over-100% warns, never blocks (D4); a day-off collision is the one hard stop
 * (surfaced verbatim). Advisory mentor gate (confirmSkillGate: true).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  bulkAssignFromTask,
  listAssignableUsers,
  AbsenceDayConflictError,
  type AssignableUser,
} from '../../../../services/scheduleAssignments';
import { unassignUserFromTask, type TaskWithBookings, type TaskAssignee } from '../../../../services/scheduleTasks';
import { setRosterAllocation, type RosterSource } from '../../../../services/scheduleRoster';
import {
  fetchEmployeeWeeks,
  type EmployeeWeekCell,
  type CrossScenarioSource,
} from '../../../../services/scheduleEmployeeSchedule';
import { fetchSkills, fetchTaskTypes } from '../../../../services/scheduleTaxonomy';
import { MENTOR_DEFAULT_PERCENT } from '../../../../utils/scheduleAssignmentDefaults';
import { orderBySpareCapacity } from '../../../../utils/rosterAllocation';
import { weekStartsInRange, weekOverlapsRange } from '../../../../utils/weekUtils';
import { tasksRunningInWeek } from './taskRefineModel';
import { sortCandidatesForTask, type StaffCandidate } from './staffSkillModel';
import { weeksToFillOnAssign, FUTURE_HORIZON_WEEKS } from './staffCapacityModel';
import { useSourceRoster } from './useSourceRoster';
import { mergeEmployeeRows, type EmployeeRow } from './staffRosterRows';
import { addWeeksISO } from '../../../../components/schedule/employeeScheduleShared';

/** A one-click mentor the nudge can add to cover a junior. */
export interface MentorCandidate {
  userKey: string;
  displayName: string;
  allocationPercent: number;
}

/** Per-task-type skill snapshot. */
interface TypeSkill {
  users: AssignableUser[];
  levelByUser: Map<string, number | null>;
  skillRequired: boolean;
  mentors: AssignableUser[];
}

/** Public per-task skill view for a card. */
export interface TaskSkillInfo {
  /** The task requires a recorded skill (gate applies). */
  skillRequired: boolean;
  /** Display name of the required skill, or null (none / not resolved yet). */
  skillName: string | null;
  /** This person's level for the required skill (null = unrated / loading). */
  levelFor: (userKey: string) => number | null;
}

interface UseStaffJobModelArgs {
  source: RosterSource | null;
  sourceKey: string | null;
  scenarioKey: string | null;
  tasks: TaskWithBookings[];
  assignmentMap: Record<string, TaskAssignee[]>;
  locked: boolean;
  onTaskPresenceChanged: (taskKey: string) => void;
  onAllocationChanged: () => void;
  capacityRefreshKey: number;
  /**
   * The source's pooled index-test allocations (2026-07-29): their windows fold
   * into the PROJECT WINDOW — an index test running past the machine tasks
   * (e.g. tasks end Aug 5, Atterberg index runs Aug 13) must still get an
   * editable project week, not land in the read-only "beyond project" zone.
   */
  indexAllocations?: Array<{ StartDate: string; EndDate: string }>;
  /**
   * P7 (2026-07-28 meeting): the read-only "beyond project" horizon length —
   * extendable by the timeline's arrow ("they need more than five weeks").
   * Defaults to FUTURE_HORIZON_WEEKS.
   */
  futureHorizonWeeks?: number;
}

/** Assignment signature so the capacity API re-fetches on ANY presence change. */
function assignmentSignature(map: Record<string, TaskAssignee[]>): string {
  const parts: string[] = [];
  for (const [taskKey, rows] of Object.entries(map)) {
    for (const r of rows) parts.push(`${taskKey}:${r.UserKey}:${r.WeekStart}:${r.Percent}`);
  }
  return parts.sort().join('|');
}

/** The BST week-starts the task runs (from its span / bookings). */
function weeksForTask(task: TaskWithBookings, weekColumns: string[]): string[] {
  return weekColumns.filter((ws) => tasksRunningInWeek([task], ws).length > 0);
}

export interface UseStaffJobModelResult {
  /** The task-span (project window) weeks PLUS the read-only future horizon. */
  weekColumns: string[];
  /** Just the project-window weeks — the editable subset of weekColumns. */
  projectWeekColumns: string[];
  /** True when `weekStart` is inside the project window (editable). */
  isProjectWeek: (weekStart: string) => boolean;
  employeeRows: EmployeeRow[];
  capacityCells: EmployeeWeekCell[];
  crossSources: Record<string, CrossScenarioSource[]>;
  cellFor: (userKey: string, weekStart: string) => EmployeeWeekCell | undefined;
  sourcePercentFor: (userKey: string, weekStart: string) => number;
  /** True when the roster has ANY row (allocation or presence) for (user, week) — 2026-07-28 ghost-row fix. */
  hasRosterEntry: (userKey: string, weekStart: string) => boolean;
  teamTotalByWeek: Map<string, number>;
  allocPeakByUser: Map<string, number>;
  candidates: AssignableUser[];
  existingKeys: Set<string>;
  loadingCapacity: boolean;
  /** Per-task skill view for the card badges + picker. */
  taskSkill: (task: TaskWithBookings) => TaskSkillInfo;
  /** Candidates for a task's picker, qualified-first then least-loaded. */
  candidatesForTask: (task: TaskWithBookings) => StaffCandidate[];
  /** Least-loaded L3+ not already on the task (the mentor nudge suggestion). */
  mentorSuggestionForTask: (task: TaskWithBookings) => MentorCandidate | null;
  // ── Writes ──
  busyTaskKey: string | null;
  assignToTask: (task: TaskWithBookings, userKey: string) => void;
  removeFromTask: (taskKey: string, userKey: string) => void;
  /** Index staffing (2026-07-28): count-once %-fill for an allocation window (never materializes task presence). */
  fillSourceShareForWindow: (userKey: string, startDate: string, endDate: string) => Promise<void>;
  /** Refetch the roster (Team capacity) — index unassign clears auto-filled shares server-side. */
  reloadRoster: () => void;
  addMentorToTask: (task: TaskWithBookings, mentorUserKey?: string) => void;
  /** Reload every task's assignees (after a roster add materializes cross-task presence). */
  reloadAllTaskAssignees: () => void;
  // ── Roster (for the capacity expander) ──
  setAllocation: (userKey: string, displayName: string, weekStart: string, percent: number) => void;
  removeMember: (userKey: string, weekStart: string) => void;
  // ── Errors ──
  error: string | null;
  clearError: () => void;
  rosterMutationError: string | null;
  clearRosterMutationError: () => void;
}

export function useStaffJobModel(args: UseStaffJobModelArgs): UseStaffJobModelResult {
  const {
    source, sourceKey, scenarioKey, tasks, assignmentMap, locked,
    onTaskPresenceChanged, onAllocationChanged, capacityRefreshKey,
    indexAllocations,
    futureHorizonWeeks = FUTURE_HORIZON_WEEKS,
  } = args;

  // The task-span (project window) weeks PLUS a read-only FUTURE HORIZON past
  // the last task week (SPEC.md §2 "beyond project — availability outlook"),
  // so a scheduler can see where a busy person frees up beyond this job.
  // 2026-07-29: index-test allocation windows fold into the span — an index
  // test scheduled past the machine tasks still belongs to the project window.
  const { weekColumns, projectWeekColumns, projectWeekSet } = useMemo(() => {
    let minStart: string | null = null;
    let maxEnd: string | null = null;
    for (const t of tasks) {
      if (t.StartDate && (!minStart || t.StartDate < minStart)) minStart = t.StartDate;
      if (t.EndDate && (!maxEnd || t.EndDate > maxEnd)) maxEnd = t.EndDate;
    }
    for (const a of indexAllocations ?? []) {
      if (a.StartDate && (!minStart || a.StartDate < minStart)) minStart = a.StartDate;
      if (a.EndDate && (!maxEnd || a.EndDate > maxEnd)) maxEnd = a.EndDate;
    }
    const projectWeeks = minStart && maxEnd ? weekStartsInRange(minStart, maxEnd) : [];
    if (projectWeeks.length === 0) {
      return { weekColumns: [] as string[], projectWeekColumns: [] as string[], projectWeekSet: new Set<string>() };
    }
    const future: string[] = [];
    let cur = projectWeeks[projectWeeks.length - 1];
    for (let i = 0; i < futureHorizonWeeks; i++) {
      cur = addWeeksISO(cur, 1);
      future.push(cur);
    }
    return {
      weekColumns: [...projectWeeks, ...future],
      projectWeekColumns: projectWeeks,
      projectWeekSet: new Set(projectWeeks),
    };
  }, [tasks, indexAllocations, futureHorizonWeeks]);

  const isProjectWeek = useCallback((weekStart: string) => projectWeekSet.has(weekStart), [projectWeekSet]);

  // ── Roster (the count-once allocation surface) ──────────────────────────────
  const {
    weeks: rosterWeeks,
    setAllocation,
    removeMember,
    reload: rosterReload,
    mutationError: rosterMutationError,
    clearMutationError: clearRosterMutationError,
  } = useSourceRoster({ source, sourceKey, scenarioKey, enabled: !locked, onAllocationChanged });

  const rosterByUserWeek = useMemo(() => {
    const m = new Map<string, number>();
    for (const w of rosterWeeks) {
      for (const e of w.entries) m.set(`${e.userKey.toLowerCase()}::${w.weekStart}`, e.percent);
    }
    return m;
  }, [rosterWeeks]);

  const teamTotalByWeek = useMemo(() => {
    const m = new Map<string, number>();
    for (const w of rosterWeeks) m.set(w.weekStart, w.entries.reduce((s, e) => s + (e.percent || 0), 0));
    return m;
  }, [rosterWeeks]);

  const sourcePercentFor = useCallback(
    (userKey: string, weekStart: string) => rosterByUserWeek.get(`${userKey.toLowerCase()}::${weekStart}`) ?? 0,
    [rosterByUserWeek],
  );

  // Ghost-row fix (2026-07-28): "Remove from this job" must clear every week
  // that has ANY roster entry (allocation row or presence), not just weeks with
  // percent > 0 — a leftover 0% row otherwise made the × a silent no-op.
  const rosterEntryWeeks = useMemo(() => {
    const s = new Set<string>();
    for (const w of rosterWeeks) {
      for (const e of w.entries) {
        if (e.hasAllocationRow || e.present) s.add(`${e.userKey.toLowerCase()}::${w.weekStart}`);
      }
    }
    return s;
  }, [rosterWeeks]);
  const hasRosterEntry = useCallback(
    (userKey: string, weekStart: string) => rosterEntryWeeks.has(`${userKey.toLowerCase()}::${weekStart}`),
    [rosterEntryWeeks],
  );

  const employeeRows = useMemo(
    () => mergeEmployeeRows(assignmentMap, rosterWeeks),
    [assignmentMap, rosterWeeks],
  );
  const existingKeys = useMemo(
    () => new Set(employeeRows.map((e) => e.userKey.toLowerCase())),
    [employeeRows],
  );

  // ── Capacity (the utilization OUTPUT) ───────────────────────────────────────
  const [capacityCells, setCapacityCells] = useState<EmployeeWeekCell[]>([]);
  const [crossSources, setCrossSources] = useState<Record<string, CrossScenarioSource[]>>({});
  const [loadingCapacity, setLoadingCapacity] = useState(false);
  const fetchIdRef = useRef(0);
  const assignmentSig = useMemo(() => assignmentSignature(assignmentMap), [assignmentMap]);

  const weekColsKey = weekColumns.join(',');
  useEffect(() => {
    if (weekColumns.length === 0) { setCapacityCells([]); return; }
    const fetchId = ++fetchIdRef.current;
    setLoadingCapacity(true);
    void fetchEmployeeWeeks({ startWeek: weekColumns[0], weekCount: weekColumns.length, scenarioKey: scenarioKey ?? undefined })
      .then((result) => {
        if (fetchIdRef.current !== fetchId) return;
        setCapacityCells(result.cells);
        setCrossSources(result.crossScenarioSources ?? {});
      })
      .catch(() => { if (fetchIdRef.current === fetchId) setCapacityCells([]); })
      .finally(() => { if (fetchIdRef.current === fetchId) setLoadingCapacity(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekColsKey, assignmentSig, scenarioKey, capacityRefreshKey]);

  const cellFor = useCallback((userKey: string, weekStart: string): EmployeeWeekCell | undefined => {
    const lk = userKey.toLowerCase();
    return capacityCells.find((c) => c.userKey.toLowerCase() === lk && c.weekStart === weekStart);
  }, [capacityCells]);

  const allocPeakByUser = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of capacityCells) {
      const k = c.userKey.toLowerCase();
      m.set(k, Math.max(m.get(k) ?? 0, c.allocationPercent ?? 0));
    }
    return m;
  }, [capacityCells]);

  // ── Candidates (unfiltered) for the add-person row in the expander ──────────
  const [candidates, setCandidates] = useState<AssignableUser[]>([]);
  useEffect(() => {
    let cancelled = false;
    listAssignableUsers().then((u) => { if (!cancelled) setCandidates(u); }).catch(() => { if (!cancelled) setCandidates([]); });
    return () => { cancelled = true; };
  }, []);

  // ── Per-task-type skill snapshot + required-skill names ─────────────────────
  const typeKeys = useMemo(() => {
    const s = new Set<string>();
    for (const t of tasks) if (t.TaskTypeKey) s.add(t.TaskTypeKey);
    return [...s].sort();
  }, [tasks]);

  const [skillByType, setSkillByType] = useState<Map<string, TypeSkill>>(new Map());
  useEffect(() => {
    if (typeKeys.length === 0) { setSkillByType(new Map()); return; }
    let cancelled = false;
    void Promise.all(typeKeys.map(async (tk) => {
      try {
        const users = await listAssignableUsers(tk);
        const levelByUser = new Map<string, number | null>();
        let skillRequired = false;
        const mentors: AssignableUser[] = [];
        for (const u of users) {
          levelByUser.set(u.AppUserKey.toLowerCase(), u.SkillLevel ?? null);
          if (u.SkillRequired) skillRequired = true;
          if ((u.SkillLevel ?? 0) >= 3) mentors.push(u);
        }
        return [tk, { users, levelByUser, skillRequired, mentors }] as [string, TypeSkill];
      } catch {
        return [tk, { users: [], levelByUser: new Map(), skillRequired: false, mentors: [] }] as [string, TypeSkill];
      }
    })).then((entries) => { if (!cancelled) setSkillByType(new Map(entries)); });
    return () => { cancelled = true; };
  }, [typeKeys]);

  // TaskTypeKey → required-skill display name (taxonomy join, fetched once).
  const [skillNameByType, setSkillNameByType] = useState<Map<string, string | null>>(new Map());
  useEffect(() => {
    let cancelled = false;
    void Promise.all([fetchTaskTypes(), fetchSkills()]).then(([types, skills]) => {
      if (cancelled) return;
      const nameByKey = new Map(skills.map((s) => [s.SkillKey, s.Name]));
      const m = new Map<string, string | null>();
      for (const t of types) m.set(t.TaskTypeKey, t.SkillKey ? (nameByKey.get(t.SkillKey) ?? null) : null);
      setSkillNameByType(m);
    }).catch(() => { /* names are a nicety; badges still work off the level */ });
    return () => { cancelled = true; };
  }, []);

  const taskSkill = useCallback((task: TaskWithBookings): TaskSkillInfo => {
    const ts = task.TaskTypeKey ? skillByType.get(task.TaskTypeKey) : undefined;
    const skillName = task.TaskTypeKey ? (skillNameByType.get(task.TaskTypeKey) ?? null) : null;
    return {
      skillRequired: ts?.skillRequired ?? false,
      skillName,
      levelFor: (userKey: string) => ts?.levelByUser.get(userKey.toLowerCase()) ?? null,
    };
  }, [skillByType, skillNameByType]);

  const candidatesForTask = useCallback((task: TaskWithBookings): StaffCandidate[] => {
    const ts = task.TaskTypeKey ? skillByType.get(task.TaskTypeKey) : undefined;
    const users = ts?.users ?? candidates;
    const assignedKeys = new Set((assignmentMap[task.ScheduleTaskKey] ?? []).map((a) => a.UserKey.toLowerCase()));
    const skillRequired = ts?.skillRequired ?? false;
    const list: StaffCandidate[] = users
      .filter((u) => !assignedKeys.has(u.AppUserKey.toLowerCase()))
      // P4 (2026-07-28 meeting): when the task needs a skill, UNRATED people
      // don't appear in the picker at all — "it shouldn't even show them in
      // the drop-down, it's just making it busy" (Sam). No-skill tasks keep
      // the whole roster.
      .filter((u) => !skillRequired || u.SkillLevel != null)
      .map((u) => ({
        userKey: u.AppUserKey,
        displayName: u.DisplayName,
        level: u.SkillLevel ?? null,
        allocationPercent: allocPeakByUser.get(u.AppUserKey.toLowerCase()) ?? 0,
      }));
    return sortCandidatesForTask(list, skillRequired);
  }, [skillByType, candidates, assignmentMap, allocPeakByUser]);

  const mentorSuggestionForTask = useCallback((task: TaskWithBookings): MentorCandidate | null => {
    if (!task.TaskTypeKey) return null;
    const ts = skillByType.get(task.TaskTypeKey);
    if (!ts) return null;
    const rows = assignmentMap[task.ScheduleTaskKey];
    const cands = ts.mentors
      .filter((m) => !rows?.some((r) => r.UserKey.toLowerCase() === m.AppUserKey.toLowerCase()))
      .map((m) => ({
        userKey: m.AppUserKey,
        displayName: m.DisplayName,
        allocationPercent: allocPeakByUser.get(m.AppUserKey.toLowerCase()) ?? 0,
      }));
    return orderBySpareCapacity(cands)[0] ?? null;
  }, [skillByType, assignmentMap, allocPeakByUser]);

  // ── Task writes ─────────────────────────────────────────────────────────────
  const [busyTaskKey, setBusyTaskKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const clearError = useCallback(() => setError(null), []);

  const runTaskWrite = useCallback((taskKey: string, work: () => Promise<void>) => {
    setBusyTaskKey(taskKey);
    void work()
      .then(() => { onTaskPresenceChanged(taskKey); onAllocationChanged(); })
      .catch((err: unknown) => {
        if (err instanceof AbsenceDayConflictError) setError(err.message);
        else setError(err instanceof Error ? err.message : 'Could not update the staffing.');
      })
      .finally(() => setBusyTaskKey((cur) => (cur === taskKey ? null : cur)));
  }, [onTaskPresenceChanged, onAllocationChanged]);

  const assignToTask = useCallback((task: TaskWithBookings, userKey: string) => {
    runTaskWrite(task.ScheduleTaskKey, async () => {
      await bulkAssignFromTask({
        scheduleTaskKey: task.ScheduleTaskKey,
        userPercents: [{ userKey, percent: 100 }],
        scenarioKey: scenarioKey ?? undefined,
        confirmSkillGate: true, // advisory — the nudge handles mentoring
      });
      // Sync the count-once SOURCE allocation % so assigning from a task card can
      // never leave "present-but-0%" (BUG-2026-0141): bulkAssignFromTask only
      // materializes per-task PRESENCE — it never writes EmployeeSourceAllocation.
      // Decision "assigning sets 100%": for each week the task runs where the
      // person has NO job share yet, set it to 100. A person already carrying an
      // intentional part-time share (e.g. a mentor's 25%) is left untouched — we
      // only fill the missing %, never clobber a deliberate one.
      // materializePresence:false (2026-07-28) — the % fill must NOT stamp
      // presence onto the job's OTHER tasks covering those weeks; that fan-out
      // belongs only to the roster's whole-job "Add person" flow. Presence here
      // stays exactly what bulk-from-task created: this one task.
      if (source && sourceKey) {
        const weeks = weeksToFillOnAssign(
          weeksForTask(task, weekColumns),
          (ws) => sourcePercentFor(userKey, ws),
        );
        for (const ws of weeks) {
          await setRosterAllocation({
            source, sourceKey, scenarioKey: scenarioKey ?? undefined,
            userKey, weekStart: ws, percent: 100,
            materializePresence: false,
          });
        }
        if (weeks.length > 0) rosterReload();
      }
    });
  }, [runTaskWrite, scenarioKey, source, sourceKey, weekColumns, sourcePercentFor, rosterReload]);

  const removeFromTask = useCallback((taskKey: string, userKey: string) => {
    runTaskWrite(taskKey, async () => {
      await unassignUserFromTask(assignmentMap[taskKey] ?? [], userKey);
    });
  }, [runTaskWrite, assignmentMap]);

  /**
   * Index staffing (2026-07-28): the count-once %-fill for an INDEX-allocation
   * assign — same "never present-but-0%" rule as assignToTask, but keyed off
   * the allocation's [startDate..endDate] window instead of a task's bookings.
   * materializePresence:false — the index endpoint already created the one
   * presence row; the roster PUT must never fan presence onto the job's tasks.
   */
  const fillSourceShareForWindow = useCallback(
    async (userKey: string, startDate: string, endDate: string) => {
      if (!source || !sourceKey) return;
      const windowWeeks = weekColumns.filter((ws) => weekOverlapsRange(ws, startDate, endDate));
      const weeks = weeksToFillOnAssign(windowWeeks, (ws) => sourcePercentFor(userKey, ws));
      for (const ws of weeks) {
        await setRosterAllocation({
          source, sourceKey, scenarioKey: scenarioKey ?? undefined,
          userKey, weekStart: ws, percent: 100,
          materializePresence: false,
        });
      }
      if (weeks.length > 0) rosterReload();
    },
    [source, sourceKey, scenarioKey, weekColumns, sourcePercentFor, rosterReload],
  );

  // Refresh EVERY task's assignees. A roster add (mentor / bottom "Add person")
  // materializes presence across all the job's tasks that week — so the top
  // task table (which reads task assignees) needs every task reloaded for the
  // person to register and any "Needs mentor" nag they resolve to clear.
  const reloadAllTaskAssignees = useCallback(() => {
    for (const t of tasks) onTaskPresenceChanged(t.ScheduleTaskKey);
  }, [tasks, onTaskPresenceChanged]);

  const addMentorToTask = useCallback((task: TaskWithBookings, mentorUserKey?: string) => {
    if (!source || !sourceKey) return;
    // Explicit pick from the mentor picker, else the least-loaded suggestion.
    const userKey = mentorUserKey ?? mentorSuggestionForTask(task)?.userKey;
    if (!userKey) return;
    // P5 (2026-07-28 meeting decision): the task table's mentor add is
    // TASK-SCOPED — "if you add it up there, it should be just the one; if you
    // add it down here [the roster], then it's all." Presence lands on THIS
    // task only (bulk-from-task, like assignToTask), and the count-once share
    // fills at the mentor default for weeks with no share yet —
    // materializePresence:false so nothing fans out (the whole-job mentor flow
    // lives in the roster's Add-person quick-adds).
    runTaskWrite(task.ScheduleTaskKey, async () => {
      await bulkAssignFromTask({
        scheduleTaskKey: task.ScheduleTaskKey,
        userPercents: [{ userKey, percent: 100 }],
        scenarioKey: scenarioKey ?? undefined,
        confirmSkillGate: true,
      });
      const weeks = weeksToFillOnAssign(
        weeksForTask(task, weekColumns),
        (ws) => sourcePercentFor(userKey, ws),
      );
      for (const ws of weeks) {
        await setRosterAllocation({
          source, sourceKey, scenarioKey: scenarioKey ?? undefined,
          userKey, weekStart: ws, percent: MENTOR_DEFAULT_PERCENT,
          materializePresence: false,
        });
      }
      if (weeks.length > 0) rosterReload();
    });
  }, [source, sourceKey, scenarioKey, mentorSuggestionForTask, weekColumns, sourcePercentFor, runTaskWrite, rosterReload]);

  return {
    weekColumns,
    projectWeekColumns,
    isProjectWeek,
    employeeRows,
    capacityCells,
    crossSources,
    cellFor,
    sourcePercentFor,
    hasRosterEntry,
    teamTotalByWeek,
    allocPeakByUser,
    candidates,
    existingKeys,
    loadingCapacity,
    taskSkill,
    candidatesForTask,
    mentorSuggestionForTask,
    busyTaskKey,
    assignToTask,
    removeFromTask,
    fillSourceShareForWindow,
    reloadRoster: rosterReload,
    addMentorToTask,
    reloadAllTaskAssignees,
    setAllocation,
    removeMember,
    error,
    clearError,
    rosterMutationError,
    clearRosterMutationError,
  };
}

/** Re-exported so consumers get the type without importing the shared module. */
export type { StaffCandidate } from './staffSkillModel';
